import { useQuery } from '@tanstack/react-query'
import { useTheme } from '@mui/material/styles'
import { collection, getDocs, query } from 'firebase/firestore'
import { db } from 'config/firebase'
import type { InventoryLeadStatus } from '@otw/models/leads/inventoryLead'
import {
  getInventoryBorderColor,
  getInventoryStatusLabel,
  isDefaultInventoryLeadStatus,
} from 'views/SuperAdmin/Management/LeadManagement/util/inventoryStatusHelpers'
import type { CustomLeadColumn } from './useLeadColumns'

export type CustomInventoryLeadColumn = CustomLeadColumn

interface UseInventoryLeadColumnsReturn {
  customColumns: CustomInventoryLeadColumn[]
  isLoading: boolean
  error: Error | null
  getColumnLabel: (status: string | InventoryLeadStatus | null | undefined) => string
  getColumnColor: (status: string | InventoryLeadStatus | null | undefined) => string
}

export const useInventoryLeadColumns = (): UseInventoryLeadColumnsReturn => {
  const theme = useTheme()
  const { data, isLoading, error } = useQuery<CustomInventoryLeadColumn[]>({
    queryKey: ['inventoryLeadColumns'],
    queryFn: async () => {
      const snapshot = await getDocs(query(collection(db, 'inventoryLeadColumns')))
      return snapshot.docs.map((d) => d.data() as CustomInventoryLeadColumn)
    },
    staleTime: 5 * 60 * 1000,
  })

  const customColumns = data ?? []

  const getColumnLabel = (
    status: string | InventoryLeadStatus | null | undefined,
  ): string => {
    if (status == null) return getInventoryStatusLabel(null)
    if (typeof status === 'string' && !isDefaultInventoryLeadStatus(status)) {
      const custom = customColumns.find((c) => c.id === status)
      if (custom) return custom.name
    }
    return getInventoryStatusLabel(status)
  }

  const getColumnColor = (
    status: string | InventoryLeadStatus | null | undefined,
  ): string => {
    if (status == null) return getInventoryBorderColor(null, theme)
    if (typeof status === 'string' && !isDefaultInventoryLeadStatus(status)) {
      const custom = customColumns.find((c) => c.id === status)
      if (custom) return custom.color
    }
    return getInventoryBorderColor(status, theme)
  }

  return {
    customColumns,
    isLoading,
    error: (error as Error | null) ?? null,
    getColumnLabel,
    getColumnColor,
  }
}
